import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, catchError, map, of, shareReplay } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { TokenService } from './token.service';

export interface CurrentEmployee {
  id: string;
  name: string;
}

interface EmployeePage {
  content?: Array<{ id?: string; email?: string; fullName?: string; firstName?: string; lastName?: string }>;
}

/**
 * La fiche employé (assistant) de l'utilisateur connecté.
 *
 * Retrouvée par l'email du jeton, comme le fait le backend pour décider de ce qu'un assistant voit.
 * Sert à l'interface : proposer « moi » comme seul assigné à qui ne voit que ses tâches.
 */
@Injectable({ providedIn: 'root' })
export class CurrentEmployeeService {
  private readonly http = inject(HttpClient);
  private readonly tokenService = inject(TokenService);
  private readonly employeesUrl = `${environment.apiURL}/master-data/api/v1/employees`;

  private cached?: { email: string; employee$: Observable<CurrentEmployee | null> };

  /** La fiche de l'utilisateur, ou null s'il n'en a pas. Un seul appel par utilisateur. */
  current(): Observable<CurrentEmployee | null> {
    const email = this.tokenService.getUserEmail()?.trim().toLowerCase();
    if (!email) {
      return of(null);
    }
    if (this.cached?.email !== email) {
      const params = new HttpParams().set('email', email).set('inactive', false).set('size', 20);
      this.cached = {
        email,
        employee$: this.http.get<EmployeePage>(this.employeesUrl, { params }).pipe(
          // Le filtre du backend peut être partiel : seule l'adresse exacte compte.
          map(page => page.content?.find(e => e.email?.trim().toLowerCase() === email) ?? null),
          map(e => e?.id
            ? { id: e.id, name: e.fullName || [e.firstName, e.lastName].filter(Boolean).join(' ') || email }
            : null),
          catchError(() => of(null)),
          shareReplay(1)
        ),
      };
    }
    return this.cached.employee$;
  }
}
